/**
 * GlobalSearch — Command Palette (⌘K / Ctrl+K) for issue #419.
 *
 * A single search-federated overlay that lets the user jump to any section
 * (Navegación, sourced from `config/navConfig` — the same list the sidebar
 * renders), run a Comando ("Nuevo proyecto"), or open a specific record
 * (Proyectos, Cotizaciones, Inventario, Herramientas, Clientes, Tareas).
 *
 * Built on react-bootstrap's `Modal` so we inherit its focus trap, Esc-to-close,
 * `aria-modal`/`role="dialog"`, and its `fullscreen="sm-down"` breakpoint prop
 * for the mobile full-screen overlay requirement — no need to hand-roll any
 * of that. The search input + results list layer the ARIA 1.2
 * combobox/listbox pattern on top (role="combobox" + aria-controls +
 * aria-activedescendant + role="listbox"/"option").
 *
 * Data strategy (see also `services/globalSearchService.js`):
 *  - Navigation + Commands are matched client-side (in-memory, instant).
 *  - Entities (Proyectos/Cotizaciones/Inventario/Herramientas/Clientes/Tareas)
 *    are searched server-side through the *existing* domain services
 *    (ilike-based), which already enforce RLS — never Supabase directly.
 *  - Debounced ~200ms; entity groups only fire once the query has >= 2
 *    characters to avoid dumping full tables on a 1-char query.
 *
 * @module components/search/GlobalSearch
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { searchAll, GROUP_LABELS, GROUP_ORDER, GROUP_DISPLAY_LIMIT } from '../../services/globalSearchService';
import './GlobalSearch.css';

const DEBOUNCE_MS = 200;

const EMPTY_GROUPS = {};

/** Discoverability hints shown on the zero-query state (what *can* I search?). */
const EMPTY_STATE_CHIPS = ['Proyectos', 'Cotizaciones', 'Tareas', 'Inventario', 'Herramientas', 'Clientes'];

const GlobalSearch = ({ show, onHide }) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const role = profile?.role;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [groups, setGroups] = useState(EMPTY_GROUPS);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({});
  // Category filter pill row (#420): 'all' or one of GROUP_ORDER's keys.
  const [activeFilter, setActiveFilter] = useState('all');
  // Search failed (network / RLS / server). Kept apart from "no results" on
  // purpose: telling the user their term matched nothing when the request
  // actually blew up is a lie they can't act on.
  const [searchFailed, setSearchFailed] = useState(false);
  // Bumping this re-runs the fetch effect — the "Reintentar" button.
  const [retryToken, setRetryToken] = useState(0);

  const requestIdRef = useRef(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset all transient state every time the palette opens.
  useEffect(() => {
    if (show) {
      setQuery('');
      setDebouncedQuery('');
      setGroups(EMPTY_GROUPS);
      setActiveIndex(0);
      setExpandedGroups({});
      setActiveFilter('all');
      setSearchFailed(false);
    }
  }, [show]);

  // Debounce ~200ms (issue #419 requirement).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Federated fetch. `requestIdRef` guards against an older, slower request
  // resolving after a newer one and clobbering fresher results.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    searchAll(debouncedQuery, { role })
      .then((result) => {
        if (cancelled || requestIdRef.current !== requestId) return;
        setGroups(result);
        setActiveIndex(0);
        // `errors` carries the domains whose fetch failed. The palette used
        // to render "Sin resultados para «X»" in exactly this case, because
        // a failed group degrades to an empty array and the two states were
        // indistinguishable from here.
        setSearchFailed((result?.errors?.length || 0) > 0);
        setLoading(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[GlobalSearch] búsqueda fallida', err);
        if (cancelled) return;
        setGroups(EMPTY_GROUPS);
        setSearchFailed(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, role, show, retryToken]);

  const totalCount = useMemo(
    () => GROUP_ORDER.reduce((sum, key) => sum + (groups[key]?.length || 0), 0),
    [groups]
  );

  // Category pill row (#420): only categories that actually have results in
  // the current query surface a pill; "Todos" is added separately so it's
  // always first and always present whenever there's anything to filter.
  const categoryKeysWithResults = useMemo(
    () => GROUP_ORDER.filter((key) => (groups[key]?.length || 0) > 0),
    [groups]
  );
  const showFilters = totalCount > 0;
  const pillKeys = useMemo(
    () => (showFilters ? ['all', ...categoryKeysWithResults] : []),
    [showFilters, categoryKeysWithResults]
  );

  // Defensive: if a new search makes the currently-active category empty
  // (e.g. user filtered to "Herramientas" then kept typing), fall back to
  // "Todos" instead of leaving the palette showing a blank result area.
  useEffect(() => {
    if (activeFilter !== 'all' && (groups[activeFilter]?.length || 0) === 0) {
      setActiveFilter('all');
      setActiveIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const visibleGroupKeys = useMemo(
    () => (activeFilter === 'all' ? GROUP_ORDER : [activeFilter]),
    [activeFilter]
  );

  // Flatten every currently-visible result (respecting "ver más" expansion
  // and the active category filter) into a single ordered list for keyboard
  // navigation.
  const flatResults = useMemo(() => {
    const out = [];
    visibleGroupKeys.forEach((key) => {
      const items = groups[key] || [];
      const limit = expandedGroups[key] ? items.length : GROUP_DISPLAY_LIMIT;
      items.slice(0, limit).forEach((item) => out.push(item));
    });
    return out;
  }, [groups, expandedGroups, visibleGroupKeys]);

  const handleFilterSelect = useCallback((key) => {
    setActiveFilter(key);
    setActiveIndex(0);
  }, []);

  const handleRetry = useCallback(() => {
    setSearchFailed(false);
    setRetryToken((n) => n + 1);
  }, []);

  // Tab / Shift+Tab cycle the visible pills (Windows Start menu pattern)
  // instead of leaving the palette via normal DOM tab order.
  const cycleFilter = useCallback(
    (direction) => {
      if (pillKeys.length === 0) return false;
      const currentIdx = pillKeys.indexOf(activeFilter);
      const nextIdx = (currentIdx + direction + pillKeys.length) % pillKeys.length;
      setActiveFilter(pillKeys[nextIdx]);
      setActiveIndex(0);
      return true;
    },
    [pillKeys, activeFilter]
  );

  const handleSelect = useCallback(
    (item) => {
      if (!item) return;
      onHide();
      navigate(item.path);
    },
    [navigate, onHide]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleSelect(flatResults[activeIndex]);
    } else if (event.key === 'Tab') {
      if (cycleFilter(event.shiftKey ? -1 : 1)) {
        event.preventDefault();
      }
    }
    // Escape is handled by react-bootstrap's Modal (keyboard prop, default true).
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const activeItem = flatResults[activeIndex];
  const activeOptionId = activeItem ? `global-search-option-${activeItem.id}` : undefined;
  const trimmedQuery = query.trim();

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen="sm-down"
      dialogClassName="global-search-dialog"
      contentClassName="global-search-content"
      backdropClassName="global-search-backdrop"
      onEntered={() => inputRef.current?.focus()}
      aria-label="Búsqueda global"
      data-testid="global-search-modal"
    >
      <div className="global-search-input-row">
        <i className="bi bi-search global-search-icon" aria-hidden="true" />
        <Form.Control
          ref={inputRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls="global-search-listbox"
          aria-activedescendant={activeOptionId}
          aria-label="Buscar en ProMedic"
          autoComplete="off"
          placeholder="Buscar proyectos, tareas, inventario, clientes…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          className="global-search-input"
          data-testid="global-search-input"
        />
        {loading && <Spinner animation="border" size="sm" className="global-search-spinner" aria-hidden="true" />}
        <button
          type="button"
          className="global-search-close"
          onClick={onHide}
          aria-label="Cerrar búsqueda"
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>

      {showFilters && (
        <div
          className="global-search-filters"
          role="group"
          aria-label="Filtrar por categoría"
          onKeyDown={handleKeyDown}
          data-testid="global-search-filters"
        >
          <button
            type="button"
            className={`global-search-filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
            aria-pressed={activeFilter === 'all'}
            onClick={() => handleFilterSelect('all')}
            data-testid="global-search-filter-all"
          >
            Todos
            <span className="global-search-filter-count">{totalCount}</span>
          </button>
          {categoryKeysWithResults.map((key) => (
            <button
              type="button"
              key={key}
              className={`global-search-filter-pill ${activeFilter === key ? 'active' : ''}`}
              aria-pressed={activeFilter === key}
              onClick={() => handleFilterSelect(key)}
              data-testid={`global-search-filter-${key}`}
            >
              {GROUP_LABELS[key]}
              <span className="global-search-filter-count">{groups[key].length}</span>
            </button>
          ))}
        </div>
      )}

      <div
        className="global-search-results"
        role="listbox"
        id="global-search-listbox"
        aria-label="Resultados de búsqueda"
        ref={listRef}
      >
        {/* Partial failure: some groups came back, others blew up. The
            results that DID load stay on screen — hiding them would be a
            second lie — with an honest banner on top. */}
        {searchFailed && totalCount > 0 && !loading && (
          <div className="global-search-error-banner" role="status" data-testid="global-search-error-banner">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <span>Algunos resultados no se pudieron cargar.</span>
            <button
              type="button"
              className="global-search-retry"
              onClick={handleRetry}
              data-testid="global-search-retry-partial"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Total failure. This is the state that used to render as
            "Sin resultados para «X»". */}
        {searchFailed && totalCount === 0 && !loading && (
          <div className="global-search-empty" data-testid="global-search-error">
            <span className="global-search-empty-icon" aria-hidden="true">
              <i className="bi bi-exclamation-triangle" />
            </span>
            <span className="global-search-empty-title">No se pudo completar la búsqueda</span>
            <span className="global-search-empty-sub">
              Revisá tu conexión y volvé a intentarlo. Si sigue fallando, avisá al administrador.
            </span>
            <button
              type="button"
              className="global-search-retry"
              onClick={handleRetry}
              data-testid="global-search-retry"
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
              Reintentar
            </button>
          </div>
        )}

        {totalCount === 0 && !loading && !searchFailed && (
          <div className="global-search-empty" data-testid="global-search-empty">
            <span className="global-search-empty-icon" aria-hidden="true">
              <i className={`bi ${trimmedQuery.length > 0 ? 'bi-search' : 'bi-stars'}`} />
            </span>
            {trimmedQuery.length > 0 ? (
              <>
                <span className="global-search-empty-title">Sin resultados para &ldquo;{trimmedQuery}&rdquo;</span>
                <span className="global-search-empty-sub">
                  Probá con otro término, un número de proyecto o el nombre de un cliente.
                </span>
              </>
            ) : (
              <>
                <span className="global-search-empty-title">Escribe para buscar en todo ProMedic</span>
                <span className="global-search-empty-sub">
                  Encontrá cualquier registro o ejecutá un comando sin salir del teclado.
                </span>
                <span className="global-search-empty-chips" aria-hidden="true">
                  {EMPTY_STATE_CHIPS.map((chip) => (
                    <span className="global-search-empty-chip" key={chip}>{chip}</span>
                  ))}
                </span>
              </>
            )}
          </div>
        )}

        {visibleGroupKeys.map((key) => {
          const items = groups[key] || [];
          if (items.length === 0) return null;
          const expanded = !!expandedGroups[key];
          const visible = expanded ? items : items.slice(0, GROUP_DISPLAY_LIMIT);

          return (
            <div className="global-search-group" key={key} data-testid={`global-search-group-${key}`}>
              {/* Redundant once the pill row has already narrowed to a single
                  category — the label would just repeat what the active pill
                  already says. */}
              {activeFilter === 'all' && (
                <div className="global-search-group-label">{GROUP_LABELS[key]}</div>
              )}
              {visible.map((item) => {
                const flatIndex = flatResults.findIndex((result) => result.id === item.id);
                const isActive = flatIndex === activeIndex;
                return (
                  <button
                    type="button"
                    key={item.id}
                    id={`global-search-option-${item.id}`}
                    data-index={flatIndex}
                    data-type={key}
                    role="option"
                    aria-selected={isActive}
                    className={`global-search-result ${isActive ? 'active' : ''}`}
                    onMouseEnter={() => setActiveIndex(flatIndex)}
                    onClick={() => handleSelect(item)}
                  >
                    <span className="global-search-result-icon" aria-hidden="true">
                      <i className={`bi ${item.icon}`} />
                    </span>
                    <span className="global-search-result-text">
                      <span className="global-search-result-title">{item.title}</span>
                      {item.subtitle && (
                        <span className="global-search-result-subtitle">{item.subtitle}</span>
                      )}
                    </span>
                    <span className="global-search-result-enter" aria-hidden="true">
                      <i className="bi bi-arrow-return-left" />
                    </span>
                  </button>
                );
              })}
              {items.length > GROUP_DISPLAY_LIMIT && !expanded && (
                <button
                  type="button"
                  className="global-search-more"
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [key]: true }))}
                >
                  <i className="bi bi-chevron-down" aria-hidden="true" />
                  Ver {items.length - GROUP_DISPLAY_LIMIT} más
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Keyboard legend + result count. Hidden below `sm` via CSS (no physical
          keyboard on a phone); aria-hidden so screen readers don't read the
          decorative kbd glyphs — the listbox already announces position. */}
      <div className="global-search-hint" aria-hidden="true">
        <span><kbd>&uarr;</kbd><kbd>&darr;</kbd> navegar</span>
        <span><kbd>Enter</kbd> ir</span>
        <span><kbd>Esc</kbd> cerrar</span>
        {totalCount > 0 && (
          <span className="global-search-hint-count">
            {totalCount} {totalCount === 1 ? 'resultado' : 'resultados'}
          </span>
        )}
      </div>
    </Modal>
  );
};

export default GlobalSearch;
